import { toPrefix } from "@/utils/workspace-prefix";
import { isNoopPayrollExecuteScript } from "@/web-archive/payrollArchiveAllowlist";
import type { ZipEngagementFs } from "./zipFilesystem";
import {
  extractMappingDictFromArchiveJson,
  processTrialBalanceMappingDict,
} from "./trialBalanceMappingArchive";
import EngagementAcceptanceJson from "@/constants/EngagementAcceptance.json";
import FraudRiskJson from "@/constants/FraudRisk.json";
import ItRiskJson from "@/constants/ItRisk.json";
import MaterialityJson from "@/constants/Materiality.json";
import PrelimAnlyticalJson from "@/constants/PrelimAnlytical.json";
import UnderstandingEntityJson from "@/constants/understandingEntity.json";
import CombinedJson from "@/data/Combined.json";

const blobRegistry = new Map<string, string>();

export function revokeWebArchiveBlobUrls(): void {
  for (const u of blobRegistry.keys()) URL.revokeObjectURL(u);
  blobRegistry.clear();
}

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

function mimeForFileName(fileName: string): string {
  const e = extOf(fileName);
  const m: Record<string, string> = {
    ".pdf": "application/pdf",
    ".png": "image/png",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".txt": "text/plain",
    ".csv": "text/csv",
    ".json": "application/json",
    ".docx":
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".xlsx":
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".pptx":
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  };
  return m[e] || "application/octet-stream";
}

function isJsonFileName(name: string): boolean {
  return extOf(String(name || "")) === ".json";
}

function extFromFileName(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : "";
}

function fileNameFromUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const fromPath = u.pathname.split("/").filter(Boolean).pop();
    if (fromPath) return decodeURIComponent(fromPath);
  } catch {
    // Ignore parse errors; fallback below.
  }
  return "downloaded-file";
}

function triggerDownload(url: string, fileName?: string): void {
  const resolvedName =
    fileName && fileName.trim().length > 0
      ? fileName
      : blobRegistry.get(url) || fileNameFromUrl(url);
  const a = document.createElement("a");
  a.href = url;
  a.download = resolvedName;
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function blobUrlForRelative(fs: ZipEngagementFs, rel: string): string | null {
  const bytes = fs.getBytes(rel);
  if (!bytes) return null;
  const name = rel.split("/").pop() || "file";
  const blob = new Blob([bytes], { type: mimeForFileName(name) });
  const url = URL.createObjectURL(blob);
  blobRegistry.set(url, name);
  return url;
}

/** Map Graph-style path `FY/juggernaut/foo` or `FY/client/foo` to zip-relative paths */
function resolveGraphPath(fs: ZipEngagementFs, filePath: string): string | null {
  const norm = filePath.replace(/\\/g, "/").replace(/^\/+/, "");
  const parts = norm.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[0] === fs.fyYear) {
    const rest = parts.slice(1).join("/");
    if (fs.resolveExisting(rest)) return rest;
  }
  if (fs.resolveExisting(norm)) return norm;
  const restJoin = parts.join("/");
  if (fs.resolveExisting(restJoin)) return restJoin;
  return null;
}

function readDb(fs: ZipEngagementFs): any {
  return fs.readJuggernautJson("db.json") || { client: [], juggernaut: [], tools: [], rbin: [] };
}

const WEB_ARCHIVE_TEMP_MARKER = "__web_archive__";

/** Matches scripts/download_*.py juggernaut filenames (Execution substantive outputs). */
const WEB_ARCHIVE_DOWNLOAD_JUGGERNAUT_FILE: Record<string, string> = {
  download_ppe_comp_anal: "Execution_PPE_CompAnal.json",
  download_ppe_adl: "Execution_PPE_ADL.json",
  download_ppe_alcomp_anls: "Execution_PPE_ALCompAnls.json",
  download_ppe_dlcomp_anls: "Execution_PPE_DLCompAnls.json",
  download_ppe_additions_composition_analysis: "Execution_PPE_ALCompAnls.json",
  download_ppe_deletions_composition_analysis: "Execution_PPE_DLCompAnls.json",
  download_ppe_composition_analysis: "Execution_PPE_CompAnls.json",
  download_ppe_cwip_composition_analysis: "Execution_PPE_CWIPCompAnls.json",
  download_ia_composition_analysis: "Execution_IT_CompAnls.json",
  download_it_alcomp_anls: "Execution_IT_ALCompAnls.json",
  download_it_dlcomp_anls: "Execution_IT_DLCompAnls.json",
  download_tailoring_questions_responses: "Execution_TailoringQuestions_Responses.json",
};

function fileNameFromAnyPath(filePath: string): string {
  const norm = filePath.replace(/\\/g, "/");
  const parts = norm.split("/").filter(Boolean);
  return parts.pop() || "";
}

/** Minimal payroll + temp-file behavior so Ageing / Composition modules read juggernaut files from the ZIP. */
function createWebArchivePayrollBridge(fs: ZipEngagementFs) {
  let handlers: Array<(p: unknown) => void> = [];
  const notify = (payload: unknown) => {
    for (const h of [...handlers]) {
      try {
        h(payload);
      } catch {
        /* ignore subscriber errors */
      }
    }
  };

  return {
    acceptedInputs: async () => [],
    listResults: async () => [],
    downloadResult: async () => ({ ok: false, error: "Not available in web archive mode" }),
    openDialog: async () => [],
    saveTempFile: async () => ({ ok: false, error: "Not available in web archive mode" }),
    getTempDir: async () => ({ ok: true, tempDir: WEB_ARCHIVE_TEMP_MARKER }),
    loadExcelColumns: async (fileName: string) => {
      const raw = String(fileName || "").trim();
      const baseName = raw.includes(" (") ? raw.split(" (")[0].trim() : raw;
      if (!baseName) return { ok: false, error: "File name required" };
      let bytes: Uint8Array | null = null;
      for (const folder of ["client", "juggernaut"] as const) {
        bytes = fs.getBytes(`${folder}/${baseName}`);
        if (bytes) break;
      }
      if (!bytes) {
        return { ok: false, error: `File not found in archive: ${baseName}` };
      }
      try {
        const XLSX = await import("xlsx");
        const wb = XLSX.read(bytes, { type: "array" });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) return { ok: false, error: "No sheets in workbook" };
        const sheet = wb.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        }) as unknown[];
        const first = Array.isArray(rows?.[0]) ? (rows[0] as unknown[]) : [];
        const columns = first.map((c) => String(c ?? "").trim()).filter(Boolean);
        if (columns.length === 0) return { ok: false, error: "No header row found" };
        return { ok: true, columns };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : "Failed to parse spreadsheet" };
      }
    },
    runTrdrcvEclFlowYoy: async () => ({ ok: false, error: "Not available in web archive mode" }),
    runTrdrcvEclFlowQoq: async () => ({ ok: false, error: "Not available in web archive mode" }),
    runTrdrcvEclYoyThreshold: async () => ({ ok: false, error: "Not available in web archive mode" }),
    runTrdrcvEclQoqThreshold: async () => ({ ok: false, error: "Not available in web archive mode" }),
    onProgress: (cb: (p: unknown) => void) => {
      handlers.push(cb);
      return () => {
        handlers = handlers.filter((x) => x !== cb);
      };
    },
    run: async (scriptKey: string, params?: { inputFiles?: unknown[]; options?: Record<string, unknown> }) => {
      const runId = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const options = (params?.options ?? {}) as Record<string, unknown>;

      queueMicrotask(() => {
        if (scriptKey === "execute_ageing_analysis_download_csv") {
          const area = String(options.area ?? "Execution");
          const section = String(options.section ?? "");
          const fileNameOpt = String(options.file_name ?? "").trim();

          let downloadFileName = fileNameOpt;
          if (!downloadFileName) {
            const sectionsData = fs.readJuggernautJson("Sections.json") as
              | Record<string, Record<string, string>>
              | null;
            const sectionCode = sectionsData?.[area]?.[section];
            if (!sectionCode) {
              const errObj = {
                success: false,
                error: `Section '${section}' not found under '${area}' in Sections.json`,
                message: "Failed to download CSV",
              };
              const errLine = JSON.stringify(errObj);
              notify({ runId, status: "running", stdout: errLine });
              notify({ runId, status: "error", error: errObj.error, stdout: errLine });
              return;
            }
            downloadFileName = `${area}_${sectionCode}_Ageing.csv`;
          }

          const text = fs.readText(`juggernaut/${downloadFileName}`);
          if (text == null) {
            notify({
              runId,
              status: "error",
              error: `File not found in archive: juggernaut/${downloadFileName}`,
              stdout: "",
            });
            return;
          }

          const fakePath = `${WEB_ARCHIVE_TEMP_MARKER}\\${downloadFileName}`;
          const result = {
            success: true,
            file_path: fakePath,
            message: "CSV downloaded successfully",
          };
          const stdout = JSON.stringify(result);
          notify({ runId, status: "running", stdout });
          notify({ runId, status: "success", stdout });
          return;
        }

        if (scriptKey === "execute_ageing_analysis_list_files") {
          const area = String(options.area ?? "Execution");
          const section = String(options.section ?? "");
          if (!section) {
            notify({ runId, status: "error", error: "No section provided", stdout: "" });
            return;
          }
          const sectionsData = fs.readJuggernautJson("Sections.json") as
            | Record<string, Record<string, string>>
            | null;
          const sectionCode = sectionsData?.[area]?.[section];
          if (!sectionCode) {
            const errLine = JSON.stringify({
              success: false,
              error: `Section '${section}' not found under '${area}'`,
              files: [],
            });
            notify({ runId, status: "error", error: `Section '${section}' not found`, stdout: errLine });
            return;
          }
          const sectionKey = `${area}_${sectionCode}_Ageing`;
          const data = readDb(fs);
          const juggernaut = Array.isArray(data?.juggernaut) ? data.juggernaut : [];
          const matchingFiles = juggernaut
            .filter(
              (entry: { section?: string[]; name?: string }) =>
                Array.isArray(entry?.section) && entry.section.includes(sectionKey),
            )
            .map((entry: { name?: string }) => entry.name)
            .filter((n: string | undefined): n is string => Boolean(n && String(n).trim()));
          const listResult = {
            success: true,
            files: matchingFiles,
            section_key: sectionKey,
            message: `Found ${matchingFiles.length} file(s) for section '${sectionKey}'`,
          };
          const stdout = JSON.stringify(listResult);
          notify({ runId, status: "running", stdout });
          notify({ runId, status: "success", stdout });
          return;
        }

        if (scriptKey === "execute_ageing_analysis_check_file_exists") {
          const fileToCheck = String(options.file_name ?? "").trim();
          const folderName = String(options.folder_name ?? "juggernaut") || "juggernaut";
          if (!fileToCheck) {
            notify({ runId, status: "error", error: "No file_name provided", stdout: "" });
            return;
          }
          const data = readDb(fs);
          const folder = Array.isArray(data?.[folderName]) ? data[folderName] : [];
          const fileExists = folder.some((entry: { name?: string }) => entry?.name === fileToCheck);
          const checkResult = {
            success: true,
            file_exists: fileExists,
            file_name: fileToCheck,
            message: `File '${fileToCheck}' ${fileExists ? "exists" : "does not exist"}`,
          };
          const stdout = JSON.stringify(checkResult);
          notify({ runId, status: "running", stdout });
          notify({ runId, status: "success", stdout });
          return;
        }

        if (isNoopPayrollExecuteScript(scriptKey)) {
          const line = JSON.stringify({
            success: true,
            message: "Archive mode: procedure skipped; use outputs already stored in the engagement ZIP.",
          });
          notify({ runId, status: "running", stdout: line });
          notify({ runId, status: "success", stdout: line });
          return;
        }

        let juggJsonFile: string | null = WEB_ARCHIVE_DOWNLOAD_JUGGERNAUT_FILE[scriptKey] ?? null;
        if (scriptKey === "download_generic_composition_analysis") {
          juggJsonFile = String(options.output_json_name ?? "").trim() || null;
          if (!juggJsonFile) {
            notify({ runId, status: "error", error: "output_json_name is required", stdout: "" });
            return;
          }
        }

        if (juggJsonFile && scriptKey.startsWith("download_")) {
          let data: unknown = fs.readJuggernautJson(juggJsonFile);
          if (data == null) data = fs.readJson(`client/${juggJsonFile}`);
          if (data == null) data = {};
          if (scriptKey === "download_tailoring_questions_responses" && !Array.isArray(data)) {
            data = [];
          }
          const line = JSON.stringify({
            success: true,
            data,
            message: `Downloaded ${juggJsonFile} from archive`,
          });
          notify({ runId, status: "running", stdout: line });
          notify({ runId, status: "success", stdout: line });
          return;
        }

        notify({
          runId,
          status: "error",
          error: `Script not available in web archive mode: ${scriptKey}`,
          stdout: "",
        });
      });

      return { ok: true, runId };
    },
  };
}

function createWebArchiveReadTempFile(fs: ZipEngagementFs) {
  return async (filePath: string) => {
    const name = fileNameFromAnyPath(filePath);
    if (!name) {
      return { success: false, error: "Invalid path" };
    }
    for (const folder of ["juggernaut", "client"] as const) {
      const text = fs.readText(`${folder}/${name}`);
      if (text != null) {
        return { success: true, content: text };
      }
    }
    return { success: false, error: "File not found in archive" };
  };
}

function readSignoff(fs: ZipEngagementFs): any {
  return fs.readJuggernautJson("Signoff.json") || { signoff: [] };
}

function getDefaultPlanningSection(sectionKey: string): any {
  switch (sectionKey) {
    case "engagement-acceptance":
      return EngagementAcceptanceJson;
    case "fraud-risk":
      return FraudRiskJson;
    case "it-risk":
      return ItRiskJson;
    case "materiality":
      return MaterialityJson;
    case "preliminary-analytical":
      return PrelimAnlyticalJson;
    case "understanding-entity":
      return UnderstandingEntityJson;
    default:
      return {};
  }
}

function readPlanningResponses(fs: ZipEngagementFs): Record<string, any> {
  const planning = fs.readJuggernautJson("Planning_UserResponses.json") as any;
  if (planning?.responses && typeof planning.responses === "object") {
    return planning.responses;
  }
  if (planning && typeof planning === "object") return planning;
  return {};
}

function workspacePrefixFromSections(workspace: string, sections: any): string {
  const forced: Record<string, string> = {
    "Other Financial Liabilities": "OFL",
    "Right-of-Use Assets": "ROU",
  };
  if (forced[workspace]) return forced[workspace];
  for (const area of ["Execution", "Planning", "ConcludingProcedures"]) {
    const block = sections?.[area];
    if (block && typeof block[workspace] === "string") {
      return String(block[workspace])
        .replace(/^Execution_/i, "")
        .trim()
        .toUpperCase();
    }
  }
  return toPrefix(workspace);
}

function filterLedgerData(data: any, filters: { fs_sub_line_id?: number; note_line_id?: number }) {
  let filteredData = Array.isArray(data?.data) ? data.data : [];
  if (filters.fs_sub_line_id) {
    filteredData = filteredData.filter(
      (item: any) =>
        item.fs_sub_line_id === filters.fs_sub_line_id ||
        item.fsSubLineId === filters.fs_sub_line_id,
    );
  }
  if (filters.note_line_id) {
    filteredData = filteredData.filter(
      (item: any) =>
        item.note_line_id === filters.note_line_id ||
        item.noteLineId === filters.note_line_id,
    );
  }
  return filteredData.map((item: any) => ({
    ...item,
    ledger_name:
      String(item.ledger_name || item.ledgerName || "")
        .replace(/_x000D/g, "")
        .replace(/\n/g, "")
        .replace(/_+$/, "")
        .trim() || "",
  }));
}

function loadBankLedgersFromMapping(dataJson: any, frf: string | null) {
  const TARGET_FS_SUB_LINE_IDS =
    frf === "Indian Accounting Standards (Ind AS)"
      ? new Set([20014, 20049])
      : new Set([20023, 20008]);
  const results: Array<{ ledger_name: string; latest_date: string; amount: number }> = [];
  for (const [, ledgers] of Object.entries(dataJson || {})) {
    if (!Array.isArray(ledgers)) continue;
    for (const item of ledgers) {
      const fsSubLineId = (item as any).fs_sub_line_id || (item as any).fsSubLineId;
      if (fsSubLineId && TARGET_FS_SUB_LINE_IDS.has(fsSubLineId)) {
        const amountDict = (item as any).amount || {};
        if (!amountDict || typeof amountDict !== "object" || Object.keys(amountDict).length === 0)
          continue;
        const parseDate = (dateStr: string): Date => {
          const [day, month, year] = dateStr.split("/");
          return new Date(2000 + parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        };
        const dates = Object.keys(amountDict);
        const latestDate = dates.reduce((latest, current) => {
          try {
            const latestDateObj = parseDate(latest);
            const currentDateObj = parseDate(current);
            return currentDateObj > latestDateObj ? current : latest;
          } catch {
            return latest;
          }
        }, dates[0]);
        const latestAmount = parseFloat(amountDict[latestDate]) || 0;
        let ledgerName = String((item as any).ledger_name || (item as any).ledgerName || "");
        ledgerName = ledgerName
          .replace(/_x000D/g, "")
          .replace(/\n/g, "")
          .replace(/_+$/, "")
          .trim();
        if (ledgerName) {
          results.push({ ledger_name: ledgerName, latest_date: latestDate, amount: latestAmount });
        }
      }
    }
  }
  return results;
}

function loadPfLedgersFromMapping(dataJson: any, filterId: number) {
  const TARGET_NOTE_LINE_IDS = new Set([filterId]);
  const results: Array<{ ledger_name: string; latest_date: string; amount: number }> = [];
  for (const [, ledgers] of Object.entries(dataJson || {})) {
    if (!Array.isArray(ledgers)) continue;
    for (const item of ledgers) {
      const noteLineId = (item as any).note_line_id || (item as any).noteLineId;
      if (noteLineId && TARGET_NOTE_LINE_IDS.has(noteLineId)) {
        const amountDict = (item as any).amount || {};
        if (!amountDict || typeof amountDict !== "object" || Object.keys(amountDict).length === 0)
          continue;
        const parseDate = (dateStr: string): Date => {
          const [day, month, year] = dateStr.split("/");
          return new Date(2000 + parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        };
        const dates = Object.keys(amountDict);
        const latestDate = dates.reduce((latest, current) => {
          try {
            return parseDate(current) > parseDate(latest) ? current : latest;
          } catch {
            return latest;
          }
        }, dates[0]);
        const latestAmount = parseFloat(amountDict[latestDate]) || 0;
        let ledgerName = String((item as any).ledger_name || (item as any).ledgerName || "");
        ledgerName = ledgerName
          .replace(/_x000D/g, "")
          .replace(/\n/g, "")
          .replace(/_+$/, "")
          .trim();
        if (ledgerName) {
          results.push({ ledger_name: ledgerName, latest_date: latestDate, amount: latestAmount });
        }
      }
    }
  }
  return results;
}

function noopOk(_data?: any) {
  return Promise.resolve({ success: true, ...(_data && { data: _data }) });
}

export function createSharePointWebShim(fs: ZipEngagementFs) {
  const engagementDetails = fs.getEngagementDetails();

  async function jsonFromJuggernaut(file: string, empty: any = {}) {
    const data = fs.readJuggernautJson(file);
    if (data == null) return { success: true, data: empty };
    return { success: true, data };
  }

  function readJuggernautOrClientJson(file: string): unknown | null {
    return fs.readJuggernautJson(file) ?? fs.readJson(`client/${file}`);
  }

  async function downloadClientJson(fileName: string) {
    const base =
      String(fileName || "")
        .trim()
        .replace(/\\/g, "/")
        .split("/")
        .filter(Boolean)
        .pop() || "";
    if (!base) return { success: true, data: {} };
    let data: unknown = fs.readJuggernautJson(base);
    if (data == null) data = fs.readJson(`client/${base}`);
    if (data == null) return { success: true, data: {} };
    return { success: true, data };
  }

  function docListWithLocalBlobs(
    rows: Array<{ name?: string; url?: string; reference?: string }>,
    templates: any,
  ) {
    return rows.map((file) => {
      const name = file?.name || "";
      let url = file?.url || "";
      if (name) {
        const local = blobUrlForRelative(fs, `client/${name}`);
        if (local) url = local;
      }
      return {
        name,
        url,
        reference: file?.reference || "",
        prep_so: templates.prep_so,
        rev_so: templates.rev_so,
        conc_so: templates.conc_so,
        eqr_so: templates.eqr_so,
        partner_so: templates.partner_so,
      };
    });
  }

  const shim: Record<string, any> = {
    storeConfig: async () => ({ success: true }),

    getFrf: async () => {
      const frf =
        (engagementDetails?.frf as string) ||
        (engagementDetails?.financial_reporting_framework as string) ||
        "";
      const reporting_date =
        (engagementDetails?.rep_date as string) ||
        (engagementDetails?.repDate as string) ||
        null;
      return { success: true, frf: frf || "", reporting_date };
    },

    getReportingDate: async () => {
      const repDate =
        engagementDetails?.rep_date ??
        engagementDetails?.repDate ??
        engagementDetails?.reporting_date ??
        null;
      const engagement_type =
        (engagementDetails?.engagement_type as string) ||
        (engagementDetails?.engagementType as string) ||
        null;
      const pmRaw =
        engagementDetails?.performance_materiality ??
        engagementDetails?.performanceMateriality ??
        engagementDetails?.Performance_Materiality ??
        null;
      const performance_materiality =
        typeof pmRaw === "number"
          ? pmRaw
          : typeof pmRaw === "string" && pmRaw.trim() !== "" && Number.isFinite(Number(pmRaw))
            ? Number(pmRaw)
            : null;
      return {
        success: true,
        rep_date: repDate != null ? String(repDate) : null,
        engagement_type: typeof engagement_type === "string" ? engagement_type : null,
        performance_materiality,
      };
    },

    downloadClientJson,

    downloadSignoffJson: () => jsonFromJuggernaut("Signoff.json", { signoff: [] }),

    downloadAssignDocumentJson: () => jsonFromJuggernaut("AssignDocument.json", { data: {} }),

    downloadSectionsJson: () => jsonFromJuggernaut("Sections.json", {}),

    downloadCaroJson: () => jsonFromJuggernaut("Conclusion_CARO_Response.json", {}),

    downloadCaroApplicability: () =>
      jsonFromJuggernaut("Conclusion_CARO_Applicability.json", {}),

    downloadRommJson: () => jsonFromJuggernaut("Libraries_Romm.json", { romm_library: [] }),

    downloadRcmJson: () => jsonFromJuggernaut("Libraries_RCM.json", {}),

    downloadControlsJson: async () => {
      let data = fs.readJuggernautJson("Libraries_Controls.json");
      if (data == null) data = fs.readJuggernautJson("Libraries_InternalControlResponses.json");
      if (data == null) return { success: true, data: {} };
      return { success: true, data };
    },

    downloadControlOwnersJson: () => jsonFromJuggernaut("Libraries_ControlOwners.json", {}),

    downloadDeficiencyJson: () => jsonFromJuggernaut("Libraries_Deficiency.json", {}),

    downloadMisstatementJson: () => jsonFromJuggernaut("Libraries_Misstatement.json", {}),

    downloadLibrariesInformation: () => jsonFromJuggernaut("Libraries_Information.json", {}),

    downloadPpeSubstantiveJson: () => jsonFromJuggernaut("SubstantiveProcedures.json", {}),

    downloadAuditEntries: () => jsonFromJuggernaut("AuditEntries.json", {}),

    downloadNoteLineMappings: () => jsonFromJuggernaut("FinData_MappingData.json", {}),

    downloadFsSublineOptions: () => jsonFromJuggernaut("FinData_FsSublineOptions.json", {}),

    downloadHeadcountReconciliationJson: async () => {
      for (const f of [
        "Execution_EBE_HeadcountReconcilation.json",
        "Execution_HC_Reconciliation.json",
      ]) {
        const data = readJuggernautOrClientJson(f);
        if (data != null) return { success: true, data };
      }
      return { success: true, data: {} };
    },

    downloadIncrementAnalysisJson: async () => {
      for (const f of ["Execution_EBE_IncrementAnalysis.json", "Execution_IncrementAnalysis.json"]) {
        const data = readJuggernautOrClientJson(f);
        if (data != null) return { success: true, data };
      }
      return { success: true, data: {} };
    },

    downloadPfSalaryJson: async () => {
      for (const f of ["Execution_EBE_PFSal.json", "Execution_PF_Salary.json"]) {
        const data = readJuggernautOrClientJson(f);
        if (data != null) return { success: true, data };
      }
      return { success: true, data: {} };
    },

    downloadActuaryTestingJson: async () => {
      for (const f of ["Execution_EBE_ActuaryTesting.json", "Execution_ActuaryTesting.json"]) {
        const data = readJuggernautOrClientJson(f);
        if (data != null) return { success: true, data };
      }
      return { success: true, data: {} };
    },

    downloadPpeAdl: () => jsonFromJuggernaut("Execution_PPE_ADL.json", {}),

    downloadIaAdl: () => jsonFromJuggernaut("Execution_IA_ADL.json", {}),

    downloadLeaseAssessment: () => jsonFromJuggernaut("Execution_LEASE_LeaseAssessment.json", {}),

    downloadLeaseResponseJson: () => jsonFromJuggernaut("Execution_LEASE_Response.json", {}),

    downloadLeaseOprResponseJson: () =>
      jsonFromJuggernaut("Execution_LEASE_OprResponse.json", {}),

    downloadOthIncOpInput: () => jsonFromJuggernaut("Execution_OTHINC_OpInput.json", {}),

    downloadOthIncFinInput: () => jsonFromJuggernaut("Execution_OTHINC_FinInput.json", {}),

    downloadTrialBalanceAmounts: () =>
      jsonFromJuggernaut("FinData_TrialBalance_Amounts.json", {}),

    downloadTrialBalanceMapping: async (args?: { includeAmounts?: boolean }) => {
      const includeAmounts = args?.includeAmounts ?? false;
      const archiveNames = ["Juggernaut_Frontend.json", "FinData_TrialBalance_Mapping.json"] as const;
      let raw: unknown = null;
      for (const name of archiveNames) {
        raw = fs.readJuggernautJson(name);
        if (raw != null) break;
        raw = fs.readJson(`client/${name}`);
        if (raw != null) break;
      }
      if (raw == null) {
        return {
          success: false,
          error:
            "Trial balance mapping not found in archive. Add Juggernaut_Frontend.json or FinData_TrialBalance_Mapping.json under juggernaut/ or client/.",
          data: null,
          mappingRows: [],
          amountsData: null,
          availableDates: [],
          filePath: "",
        };
      }
      const mappingDict = extractMappingDictFromArchiveJson(raw);
      if (!mappingDict || Object.keys(mappingDict).length === 0) {
        return {
          success: false,
          error:
            "Could not parse trial balance mapping from archive JSON (expected book names → ledger entry arrays).",
          data: null,
          mappingRows: [],
          amountsData: null,
          availableDates: [],
          filePath: "",
        };
      }
      const { mappingRows, amountsData, availableDates, data } = processTrialBalanceMappingDict(
        mappingDict,
        includeAmounts,
      );
      return {
        success: true,
        mappingRows,
        amountsData: includeAmounts ? amountsData ?? [] : null,
        availableDates,
        data,
        filePath: WEB_ARCHIVE_TEMP_MARKER,
      };
    },

    loadLedgerData: async (filters: { fs_sub_line_id?: number; note_line_id?: number }) => {
      const data = fs.readJuggernautJson("FinData_TrialBalance_Mapping.json");
      if (data == null) return { success: true, data: [] };
      return { success: true, data: filterLedgerData(data, filters || {}) };
    },

    loadBankAccountLedgers: async () => {
      const dataJson = fs.readJuggernautJson("FinData_TrialBalance_Mapping.json");
      if (dataJson == null) return { success: true, data: [] };
      const frf = (engagementDetails?.frf as string) || null;
      return { success: true, data: loadBankLedgersFromMapping(dataJson, frf) };
    },

    loadPfLedgers: async (noteLineId?: number) => {
      const dataJson = fs.readJuggernautJson("FinData_TrialBalance_Mapping.json");
      if (dataJson == null) return { success: true, data: [] };
      const filterId = noteLineId ?? 30152;
      return { success: true, data: loadPfLedgersFromMapping(dataJson, filterId) };
    },

    loadAllDocuments: async () => {
      const dbData = readDb(fs);
      const clientFiles = Array.isArray(dbData?.client) ? dbData.client : [];
      const results: Array<{ name: string; url: string; reference?: string }> = [];
      for (const file of clientFiles) {
        const name = file?.name || "";
        if (isJsonFileName(name)) continue;
        let url = file?.url || "";
        if (name) {
          const blob = blobUrlForRelative(fs, `client/${name}`);
          if (blob) url = blob;
        }
        results.push({
          name,
          url,
          reference: file?.reference || "",
        });
      }
      return { success: true, data: results };
    },

    loadCloudFiles: async () => {
      const dbData = readDb(fs);
      const clientFiles = Array.isArray(dbData?.client) ? dbData.client : [];
      return {
        success: true,
        data: {
          files: clientFiles
            .filter((file: any) => !isJsonFileName(file?.name || ""))
            .map((file: any) => ({
              name: file.name || "",
              url: file.url || "",
              reference: file.reference || "",
            })),
        },
      };
    },

    getDocumentsFromFolder: async (folderName: string) => {
      const dbData = readDb(fs);
      const documents = (dbData[folderName] || []).filter(
        (file: any) => !isJsonFileName(file?.name || ""),
      );
      return { success: true, data: documents };
    },

    loadDocumentListBySection: async (sectionToFind: string) => {
      const dbData = readDb(fs);
      const signoffData = readSignoff(fs);
      let prepSoTemplate: any[];
      let revSoTemplate: any[];
      let concSoTemplate: any[];
      let eqrSoTemplate: any[];
      let partnerSoTemplate: any[];
      if (signoffData?.signoff && Array.isArray(signoffData.signoff) && signoffData.signoff.length > 0) {
        const sample = signoffData.signoff[0] || {};
        prepSoTemplate = sample.prep_so || [{ name: "", metadata: "" }];
        revSoTemplate = sample.rev_so || [{ name: "", metadata: "" }];
        concSoTemplate = sample.conc_so || [{ name: "", metadata: "" }];
        eqrSoTemplate = sample.eqr_so || [{ name: "", metadata: "" }];
        partnerSoTemplate = sample.partner_so || [{ name: "", metadata: "" }];
      } else {
        prepSoTemplate = [{ name: "", metadata: "" }];
        revSoTemplate = [{ name: "", metadata: "" }];
        concSoTemplate = [{ name: "", metadata: "" }];
        eqrSoTemplate = [{ name: "", metadata: "" }];
        partnerSoTemplate = [{ name: "", metadata: "" }];
      }
      const templates = {
        prep_so: prepSoTemplate,
        rev_so: revSoTemplate,
        conc_so: concSoTemplate,
        eqr_so: eqrSoTemplate,
        partner_so: partnerSoTemplate,
      };

      const results: any[] = [];
      const clientFiles = Array.isArray(dbData?.client) ? dbData.client : [];
      for (const file of clientFiles) {
        if (isJsonFileName(file?.name || "")) continue;
        const sections: string[] = Array.isArray(file?.section) ? file.section : [];
        const matches = sections.some((section) => section.startsWith(sectionToFind));
        if (matches) {
          results.push(
            docListWithLocalBlobs([{ name: file?.name, url: file?.url, reference: file?.reference }], templates)[0],
          );
        }
      }
      const juggernautFiles = Array.isArray(dbData?.juggernaut) ? dbData.juggernaut : [];
      for (const file of juggernautFiles) {
        if (isJsonFileName(file?.name || "")) continue;
        const sections: string[] = Array.isArray(file?.section) ? file.section : [];
        const matches = sections.some((section) => section.startsWith(sectionToFind));
        if (matches) {
          const name = file?.name || "";
          let url = file?.url || "";
          if (name) {
            const local = blobUrlForRelative(fs, `juggernaut/${name}`);
            if (local) url = local;
          }
          results.push({
            name,
            url,
            reference: file?.reference || "",
            ...templates,
          });
        }
      }
      return { success: true, data: results };
    },

    loadComcFiles: async (sectionToFind: string) => {
      const dbData = readDb(fs);
      const prefix = String(sectionToFind || "");
      const collectMatchingNames = (entries: any[]): string[] =>
        entries
          .filter((entry: any) => {
            if (isJsonFileName(entry?.name || "")) return false;
            if (!entry.section || !Array.isArray(entry.section)) return false;
            return entry.section.some((s: string) => String(s).startsWith(prefix));
          })
          .map((entry: any) => String(entry.name || "").trim())
          .filter(Boolean);

      const clientFiles = Array.isArray(dbData?.client) ? dbData.client : [];
      const juggernautFiles = Array.isArray(dbData?.juggernaut) ? dbData.juggernaut : [];
      const combined = [...collectMatchingNames(clientFiles), ...collectMatchingNames(juggernautFiles)];
      const seen = new Set<string>();
      let matches = combined.filter((n: string) => {
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      });
      if (matches.length === 0 && prefix.startsWith("Execution_EBE")) {
        const fallback = clientFiles
          .filter((entry: any) => !isJsonFileName(entry?.name || "") && String(entry?.name || "").trim())
          .map((entry: any) => String(entry.name).trim());
        matches = fallback.filter((n: string) => {
          if (seen.has(n)) return false;
          seen.add(n);
          return true;
        });
      }
      return { success: true, data: { files: matches } };
    },

    findFilesBySection: async (sectionToFind: string, folderName: string = "client") => {
      const dbData = readDb(fs);
      if (folderName && !dbData[folderName]) {
        return { success: false, error: `Folder '${folderName}' not found in db.json` };
      }
      let matches: string[] = [];
      if (folderName && dbData[folderName]) {
        matches = dbData[folderName]
          .filter((entry: any) => {
            if (isJsonFileName(entry?.name || "")) return false;
            const sections = entry.section || [];
            if (!sectionToFind || sectionToFind === "") return true;
            return sections.some((s: string) => s.startsWith(sectionToFind));
          })
          .map((entry: any) => entry.name);
      }
      return { success: true, data: { files: matches } };
    },

    getFileWebUrl: async (filePath: string) => {
      const rel = resolveGraphPath(fs, filePath);
      if (!rel) {
        return { success: false, error: `File not found in archive: ${filePath}` };
      }
      const blob = blobUrlForRelative(fs, rel);
      if (!blob) return { success: false, error: "Could not open file in archive" };
      return { success: true, data: { webUrl: blob } };
    },

    readScriptsFileBase64: async () => ({
      success: false,
      error: "Scripts bundle is not available in web archive mode",
    }),

    readRommLibrary: async () => {
      const parsed = fs.readJuggernautJson("Libraries_Romm.json") as any;
      const raw = parsed?.romm_library ?? parsed;
      const rommArray = Array.isArray(raw) ? raw : Array.isArray(raw?.romm_library) ? raw.romm_library : [];
      return { success: true, data: { romm_library: rommArray } };
    },

    readRommLibraryByWorkspace: async (workspace: string) => {
      const sections = fs.readJuggernautJson("Sections.json") as any;
      const prefix = workspacePrefixFromSections(workspace, sections);
      const parsed = fs.readJuggernautJson("Libraries_Romm.json") as any;
      const raw = parsed?.romm_library ?? parsed;
      const rommArray: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.romm_library) ? raw.romm_library : [];
      const filtered = rommArray.filter((entry: any) => {
        const rommId = String(entry["romm-id"] || "").toUpperCase();
        return rommId.startsWith(`${prefix.toUpperCase()}.`);
      });
      return { success: true, data: { romm_library: filtered } };
    },

    loadExceptionList: async (area?: string) => {
      const areaType = area || "payroll";
      const areaKeyMap: Record<string, string> = {
        payroll: "payroll_exceptions",
        ppe: "ppe_exceptions",
        intangible: "intangible_exceptions",
        tr: "tr_exceptions",
        comc: "comc_exceptions",
        tp: "tp_exceptions",
        inventory: "inventory_exceptions",
      };
      const jsonKey = areaKeyMap[areaType.toLowerCase()] || "payroll_exceptions";
      const ex = fs.readJuggernautJson("Exception_Test.json") as any;

      let exceptionsArray: any[] = [];
      if (Array.isArray(ex)) {
        const areaElement = ex.find((item: any) => item && typeof item === "object" && item[jsonKey] !== undefined);
        exceptionsArray = Array.isArray(areaElement?.[jsonKey]) ? areaElement[jsonKey] : [];
      } else if (ex && typeof ex === "object") {
        exceptionsArray = Array.isArray(ex[jsonKey]) ? ex[jsonKey] : [];
      }

      const selected = exceptionsArray.reduce((acc: number[], item: any, idx: number) => {
        const key = `exception_${idx + 1}`;
        const value = item?.[key];
        if (value === "Yes" || value === "YES") {
          acc.push(idx + 1);
        }
        return acc;
      }, []);

      return { success: true, data: selected };
    },

    fetchRelatedAccounts: async (parList: string[] = []) => {
      try {
        if (!Array.isArray(parList) || parList.length === 0) {
          return { success: true, data: [] };
        }

        const jsonData =
          fs.readJuggernautJson("FinData_TrialBalance_Mapping.json") ??
          fs.readJson("client/FinData_TrialBalance_Mapping.json");

        if (!jsonData || typeof jsonData !== "object" || Array.isArray(jsonData)) {
          return { success: true, data: [] };
        }

        const parseDate = (d: string): number => {
          const slash = String(d).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
          if (slash) {
            const day = parseInt(slash[1], 10);
            const month = parseInt(slash[2], 10) - 1;
            let year = parseInt(slash[3], 10);
            if (year < 100) year += 2000;
            return new Date(year, month, day).getTime();
          }
          const iso = Date.parse(String(d));
          return Number.isNaN(iso) ? Number.NEGATIVE_INFINITY : iso;
        };

        const rows: Array<{
          par: string;
          note_line_id: string;
          note_line: string;
          amount: number;
        }> = [];

        for (const ledgers of Object.values(jsonData as Record<string, unknown>)) {
          if (!Array.isArray(ledgers)) continue;
          for (const ledger of ledgers) {
            if (!ledger || typeof ledger !== "object") continue;
            const entry = ledger as Record<string, any>;
            const par = entry.par;
            if (!par || !parList.includes(par)) continue;

            const amountObj = entry.amount;
            if (!amountObj || typeof amountObj !== "object" || Array.isArray(amountObj)) continue;

            const dateKeys = Object.keys(amountObj).filter(Boolean);
            if (dateKeys.length === 0) continue;

            const latestDate = dateKeys.reduce((best, cur) =>
              parseDate(cur) >= parseDate(best) ? cur : best,
            );
            const amountValue = parseFloat(String(amountObj[latestDate] ?? 0));
            if (Number.isNaN(amountValue)) continue;

            rows.push({
              par: String(par),
              note_line_id: entry.note_line_id != null ? String(entry.note_line_id) : "",
              note_line: entry.note_line != null ? String(entry.note_line) : "",
              amount: amountValue,
            });
          }
        }

        const aggregated = new Map<
          string,
          { par: string; note_line_id: string; note_line: string; amount: number }
        >();
        for (const row of rows) {
          const key = `${row.par}\0${row.note_line_id}\0${row.note_line}`;
          const existing = aggregated.get(key);
          if (existing) {
            existing.amount += row.amount;
          } else {
            aggregated.set(key, { ...row });
          }
        }

        return { success: true, data: Array.from(aggregated.values()) };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Failed to fetch related accounts",
          data: [],
        };
      }
    },

    customRfGetDb: async () => ({ success: true, data: readDb(fs) }),

    generateFinancialAnalytics: async () => ({ success: false, error: "Not available in web archive mode" }),

    processRcmImport: async () => ({ success: false, error: "Not available in web archive mode" }),

    createTrialBalanceMappings: async () => ({ success: false, error: "Not available in web archive mode" }),

    extractTrialBalanceAmounts: async () => ({ success: false, error: "Not available in web archive mode" }),

    closeTrialBalance: async () => ({ success: false, error: "Not available in web archive mode" }),

    downloadArchival: async () => ({ success: false, error: "Not available in web archive mode" }),

    addRommEntry: noopOk,
    updateRommEntry: noopOk,
    uploadRommJson: noopOk,
    uploadFile: noopOk,
    uploadSignoff: noopOk,
    uploadAssignDocument: noopOk,
    removeAssignDocument: noopOk,
    uploadLeaseResponseFinance: noopOk,
    uploadLeaseOprResponse: noopOk,
    uploadCaroJson: noopOk,
    generateCaroReport: noopOk,
    uploadHeadcountReconciliationUserRows: noopOk,
    updateExceptionTestJson: noopOk,
    saveExceptionList: noopOk,
    updateFileSections: noopOk,
    removeFileSection: noopOk,
    savePpeAdlTable: noopOk,
    saveIaAdlTable: noopOk,
    uploadControlOwnersJson: noopOk,
    deleteControlOwners: noopOk,
    restoreControlOwner: noopOk,
    restoreSubstantiveProcedure: noopOk,
    deleteRomms: noopOk,
    restoreRomm: noopOk,
    deleteControls: noopOk,
    restoreControl: noopOk,
    uploadControlsJson: noopOk,
    uploadRcmJson: noopOk,
    uploadClientJson: noopOk,
    uploadDocumentLibrary: noopOk,
    deleteDocumentLibrary: noopOk,
    restoreRbinFile: noopOk,
    deletePermanentlyRbinFiles: noopOk,
    renameFile: noopOk,
    replaceFile: noopOk,
    updateFileSectionInDb: noopOk,
    saveTrialBalanceMappingChanges: noopOk,
    uploadAuditEntriesExcel: noopOk,
    saveAuditEntries: noopOk,
    addSectionWorkspace: noopOk,
    deleteSectionWorkspaces: noopOk,
    updateLibrariesInformation: noopOk,
    addLibrariesInformation: noopOk,
    uploadLibrariesInformation: noopOk,
    updateInformationEntry: noopOk,
    deleteInformationEntries: noopOk,
    restoreInformationEntry: noopOk,
    uploadDeficiencyJson: noopOk,
    uploadDeficiencyDetailJson: noopOk,
    deleteDeficiencies: noopOk,
    restoreDeficiency: noopOk,
    createDeficiency: noopOk,
    createMisstatement: noopOk,
    updateMisstatement: noopOk,
    uploadMisstatementJson: noopOk,
    deleteMisstatements: noopOk,
    restoreMisstatement: noopOk,
    customRfUploadJson: noopOk,
    customRfCopyFile: noopOk,
    customRfRebuildDbUrls: noopOk,
    deletePreviousRcmImportFiles: noopOk,
    saveTempFile: noopOk,
    openFileDialog: async () => ({ success: false, canceled: true }),
    selectFolderDialog: async () => ({ success: false, canceled: true }),
    addNoteLine: noopOk,
  };

  shim.downloadRbinJson = async (fileName: string) => {
    const data = fs.readRbinJson(fileName);
    if (data == null) return { success: true, data: {} };
    return { success: true, data };
  };

  shim.customRfDownloadJson = async (payload: { fyYear: string; fileName: string; folder?: string }) => {
    const folder = (payload.folder || "juggernaut").replace(/^\/+|\/+$/g, "");
    const rel = `${folder}/${payload.fileName}`;
    let data = fs.readJson(rel);
    if (data == null) data = fs.readJuggernautJson(payload.fileName);
    if (data == null) data = fs.readJson(`${payload.fyYear}/${folder}/${payload.fileName}`);
    if (data == null) return { success: true, data: {} };
    return { success: true, data };
  };

  return shim;
}

export function installSharePointWebShim(fs: ZipEngagementFs): void {
  revokeWebArchiveBlobUrls();
  const shim = createSharePointWebShim(fs);
  (window as any).sharePointAPI = shim;
  const planningResponses = readPlanningResponses(fs);
  const planningLocalState = new Map<string, any>();
  const tailoringExec =
    (fs.readJuggernautJson("Execution_TailoringQuestions_Responses.json") as Record<string, any>) || {};
  const tailoringConc =
    (fs.readJuggernautJson("ConcludingProcedures_TailoringQuestions_Responses.json") as Record<string, any>) || {};

  const reviewComments = fs.readJuggernautJson("Review_Comments.json") as any;
  const allComments = reviewComments?.comments ?? [];

  (window as any).reviewCommentsAPI = {
    downloadComments: async (questionId: string) => {
      const list = Array.isArray(allComments)
        ? allComments.filter((c: any) => String(c?.questionId || c?.question_id || "") === String(questionId))
        : [];
      return { success: true, comments: list };
    },
    uploadComments: async () => ({ success: true }),
    downloadAllComments: async () => ({ success: true, comments: Array.isArray(allComments) ? allComments : [] }),
  };

  (window as any).planning = {
    readSection: async (_companyName: string, sectionKey: string) => {
      if (planningLocalState.has(sectionKey)) return planningLocalState.get(sectionKey);
      return getDefaultPlanningSection(sectionKey);
    },
    saveSection: async (_companyName: string, sectionKey: string, content: unknown) => {
      planningLocalState.set(sectionKey, content);
      return { ok: true };
    },
    readCombinedData: async () => CombinedJson,
  };

  (window as any).planningAPI = {
    saveResponse: async () => ({ success: true }),
    saveAllResponses: async () => ({ success: true }),
    loadResponses: async () => ({ success: true, data: planningResponses }),
    testConnection: async () => ({ success: true }),
    getCompanyName: async () =>
      String(
        (fs.getEngagementDetails()?.client_name as string) ||
          (fs.getEngagementDetails()?.["Client Name"] as string) ||
          "ABC Private Limited",
      ),
  };

  (window as any).payrollAPI = {
    saveResponses: async () => ({ success: true }),
    loadResponses: async () => ({ success: true, data: planningResponses }),
  };

  (window as any).tailoringQuestionsAPI = {
    saveResponses: async () => ({ success: true }),
    loadResponses: async (data: { areaName?: string }) => {
      const area = String(data?.areaName || "");
      const isConcluding = /^[A-Z]{2,}$/.test(area);
      const source = isConcluding ? tailoringConc : tailoringExec;
      const value = source?.[area];
      if (value && typeof value === "object") {
        return { success: true, data: value };
      }
      return { success: true, data: source || {} };
    },
  };

  (window as any).payroll = createWebArchivePayrollBridge(fs);

  (window as any).cloud = {
    upload: async () => ({ success: false, error: "Not available in web archive mode" }),
    directUpload: async () => ({ success: false, error: "Not available in web archive mode" }),
    download: async () => ({ success: false, error: "Not available in web archive mode" }),
    list: async () => ({ success: true, files: [] }),
    delete: async () => ({ success: false, error: "Not available in web archive mode" }),
    writeTempFile: async () => ({ success: false, error: "Not available in web archive mode" }),
    readTempFile: createWebArchiveReadTempFile(fs),
    deleteTempFile: async () => ({ success: false, error: "Not available in web archive mode" }),
    onProgress: () => () => {},
  };

  const dbData = readDb(fs);
  const docRows: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    uploadTimestamp: number;
    category: string;
    mimeType: string;
    extension: string;
    contextLabel?: string;
    contextId?: string;
    relativePath: string;
  }> = [];

  const addDocRows = (entries: any[], folder: "client" | "juggernaut") => {
    if (!Array.isArray(entries)) return;
    for (const entry of entries) {
      const fileName = String(entry?.name || "");
      if (!fileName) continue;
      if (isJsonFileName(fileName)) continue;
      const relativePath = `${folder}/${fileName}`;
      const bytes = fs.getBytes(relativePath);
      const extension = extFromFileName(fileName);
      const category =
        (Array.isArray(entry?.section) && entry.section[0]) ||
        entry?.reference ||
        folder;
      docRows.push({
        id: `${folder}:${fileName}`,
        fileName,
        fileSize: bytes?.byteLength || 0,
        uploadTimestamp: Date.now(),
        category: String(category || folder),
        mimeType: mimeForFileName(fileName),
        extension,
        contextLabel: entry?.reference ? String(entry.reference) : undefined,
        contextId: undefined,
        relativePath,
      });
    }
  };

  addDocRows(dbData?.client || [], "client");
  addDocRows(dbData?.juggernaut || [], "juggernaut");

  const docsById = new Map(docRows.map((d) => [d.id, d]));
  const noopUnsub = () => {};

  (window as any).documents = {
    list: async () =>
      docRows.map(({ relativePath, ...rest }) => ({
        ...rest,
      })),
    acceptedTypes: async () => [
      ".pdf",
      ".docx",
      ".xlsx",
      ".csv",
      ".txt",
      ".json",
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".webp",
    ],
    upload: async () => [{ ok: false, error: "Not available in web archive mode" }],
    delete: async () => ({ ok: false }),
    onProgress: () => noopUnsub,
    download: async (id: string) => {
      const doc = docsById.get(id);
      if (!doc) return { ok: false, error: "Document not found" };
      const url = blobUrlForRelative(fs, doc.relativePath);
      if (!url) return { ok: false, error: "Document not found in archive" };
      triggerDownload(url, doc.fileName);
      return { ok: true, filePath: doc.fileName };
    },
    open: async (id: string) => {
      const doc = docsById.get(id);
      if (!doc) return { ok: false, error: "Document not found" };
      const url = blobUrlForRelative(fs, doc.relativePath);
      if (!url) return { ok: false, error: "Document not found in archive" };
      triggerDownload(url, doc.fileName);
      return { ok: true };
    },
  };

  (window as any).electronWindow = {
    getPlatform: async () => "web",
    minimize: async () => {},
    maximize: async () => {},
    close: async () => {},
    openExternal: async (url: string) => {
      triggerDownload(url);
      return { success: true };
    },
    logError: async () => {},
    onLogoutRequest: () => {},
    sendLogoutComplete: () => {},
  };
}
